import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import HouseholdEligibilityGroups from '@components/individualPortal/dashboard/HouseholdEligibilityGroups';
import Tooltip from '@common-components/tooltip/Tooltip';
import {
    APP_STATUSES,
    ELIGIBILITY_STATUSES,
    ELIGIBILITY_INDICATORS,
    VERIFICATION_STATUSES,
    DMI_DOCUMENT_UPLOAD_STATUS,
    ELIGIBILITY_OPTIONS,
} from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { FormattedMessage } from 'react-intl';
import { ctaHandlers } from '@utils/dashboard/stateSpecificUtils';
import { STATE_NAMES } from '@constants/configs/states';
import { configs } from '@constants/configs/configs';

import { showModal } from '@actions/common/actionCreators';
import {
    checkActiveEnrollment,
    cloneApplication
} from '@actions/individualPortal/dashboard/dashboardActionCreators';
import { renderAmount } from '@utils/dashboard/processCommon';
import { useNavigate } from 'react-router-dom';
import { useExchangeConditions } from '@hooks/index';
import { ConditionalRendering } from '@components/common/conditional-rendering';



const HouseholdEligibilityContainer = props => {
    const {
        actions,
        environment: {
            isState,
            stateCode,
            links: {
                PHONE
            },
            ssapFlowVersion,
            flags: {
                multiHouseholdEnabled,
            },
            isStateFullStack,
            isMNFullStack,
            exchangeName,
        },
        dashboardModels,
        activeTab,
    } = props;

    const navigate = useNavigate();
    
    const activeTabData = dashboardModels[activeTab];
    const {
        applicationYear,
        applicationStatus, 
        applicants,
        stateSubsidy,
        preEligibility, 
        isInsideOEWindow,
        isFinancial,
        openApplication,
        verificationStatus,
        dmiDocumentUploadstatus,
        encryptedCaseNumber,
        encryptedApplicationId,
        isLocked
    } = activeTabData;


    const { isHouseholdEligibilityConditionTooltipVisible } = useExchangeConditions();

    let applicantByTaxHouseholdGroup = [];
    if(applicants !== null) {
        applicantByTaxHouseholdGroup = Object.values(applicants.reduce((acc, x) => {
            acc[x.taxHouseholdGroup] = [...(acc[x.taxHouseholdGroup] || []), x ];
            return acc;
        }, {}));
        const sorted = true;
        applicantByTaxHouseholdGroup.sort(function(a, b) {
            if (!a[0].taxHouseholdGroup) { return -1; }

            if (!b[0].taxHouseholdGroup) { return +1; }

            if (sorted)
                return a[0].taxHouseholdGroup.localeCompare(b[0].taxHouseholdGroup);
            else
                return b[0].taxHouseholdGroup.localeCompare(a[0].taxHouseholdGroup);
        }).reverse();
    }

    const preAPTC = preEligibility && preEligibility.aptcEligible;
    const preCSR = preEligibility && preEligibility.csrEligible;
    const isPreEligibility = preAPTC || preCSR;
    
    const caseNumber = openApplication ? openApplication.caseNumber : activeTabData.caseNumber;
    const ssapApplicationId = openApplication ? openApplication.ssapApplicationId : activeTabData.ssapApplicationId;

    const ssapParams = { ssapFlowVersion, caseNumber, applicationStatus, applicationYear, isInsideOEWindow, isFinancial, ssapApplicationId, encryptedApplicationId };
    const { goToUploadDocs, goToReportAChange } = ctaHandlers;
    
    const viewDetailsCb = () => goToEligibilityResults();
    const reportAChangeCb = () => goToReportAChange(stateCode, ssapParams, actions, isStateFullStack);
    const uploadDocsCb = () => goToUploadDocs(caseNumber, stateCode, isMNFullStack);

    const goToEligibilityResults = ( CALHEERS_URL, householdCaseId) => {
        navigate(configs.routes.ELIGIBILITY_RESULTS, {
            state: {
                prevLocation: configs.routes.INDPORTAL,
                encryptedCaseNumber: encryptedCaseNumber,
            },
        });
    };
    
    const CTA_Link = ({ id, className, text, cb, href='#' }) => {

        const onClickHandler = (e) => {
            href ==='#' && e.preventDefault();
            cb();
        };

        return (
            <a
                id={id}
                className={`text-underline ${className}`}
                href={href}
                onClick={onClickHandler}
            >
                {<FormattedMessage id={text}/>}
            </a>
        );
    };


    /**
     * Retunrs CTA links (column 3).
     * @returns {*}
     * @constructor
     */
    const HouseholdCTA = () => {
        return (
            <div className="tablet:grid-col-3">
                <CTA_Link
                    id="household_elig_link"
                    className='household-eligibility_link'
                    text="indportal.dashboard.householdSection.eligibility.cta.viewDetails"
                    cb={viewDetailsCb}
                />
                <br/>

                { !isLocked &&
                    <CTA_Link
                        id="household_report_change_link"
                        className='household-eligibility_link'
                        text={'indportal.dashboard.householdSection.eligibility.cta.editApplication'}
                        cb={reportAChangeCb}
                    />
                }


                <br/>

                {multiHouseholdEnabled &&
                    <CTA_Link
                        id="household_other_tax_households"
                        className='household-eligibility_link'
                        text="indportal.dashboard.householdSection.eligibility.cta.multihousehold"
                        href={configs.routes.MULTI_HOUSEHOLD_DETAILS}
                    />
                }

            </div>);
    };

    /**
     * Helper function to get FormattedMessage.
     * @param id
     * @param values
     * @param tagName
     * @returns {*}
     */
    const formatMessage = (id, values, tagName) => <FormattedMessage id={id} values={ values } tagName={ tagName }/>;

    const checkMemberEligibility = (flagName, applicants) => applicants.some(applicant => applicant[flagName] === true);

    const groupHasTurquoiseEligibility = (group) => group && group.some(applicant => applicant.turquoiseEligible);

    /**
     * Returns conditional eligibility msg if isEligibilityConditional is true and application status is not closed.
     * @returns {*|boolean}
     */
    const getConditionEligibilitySection = () => {
        return verificationStatus === VERIFICATION_STATUSES.ACTION_NEEDED &&
            (dmiDocumentUploadstatus === DMI_DOCUMENT_UPLOAD_STATUS.PENDING || dmiDocumentUploadstatus === DMI_DOCUMENT_UPLOAD_STATUS.PROCESSING) &&
            applicationStatus !== APP_STATUSES.CL &&
            <div className="usa-alert gi-alert gi-alert__left-accent gi-alert__error household-eligibility__conditional">
                <FormattedMessage id="indportal.dashboard.householdSection.eligibilityConditional"/>

                {
                    isHouseholdEligibilityConditionTooltipVisible &&

                    <Tooltip
                        {...{
                            id: 'label.conditional',
                            content: 'indportal.dashboard.householdSection.eligibilityConditional.tooltip',
                            contentReplacement: { exchangeName: exchangeName },
                            dataAutomationId: 'household_eligibility_household_section_conditonal_tooltip'
                        }}
                    />
                }
                <CTA_Link
                    id="indportal.dashboard.householdSection.eligibilityConditional"
                    className='link'
                    text="indportal.dashboard.householdSection.moreDetails"
                    cb={uploadDocsCb}
                />
                <FormattedMessage id="indportal.dashboard.householdSection.uploadDocs"/>
            </div>;
    };
    
    /**
     * Returns public program message if at least one member of the household is Medicaid assessed/determined, BHP assessed/determined, or CHIP assessed.
     */
    const showPublicProgramMessage = () => {
        if (isState.ID || !isStateFullStack) {
            return false;
        } else if (isMNFullStack) {
            return applicants && applicants.some(applicant =>
                applicant.assessedMedicaidMAGIEligible || applicant.assessedMedicaidNonMAGIEligible || applicant.medicaidMAGIEligible || applicant.medicaidNonMAGIEligible ||
                applicant.assessedBhpEligible || applicant.bhpEligible);
        } else {
            return applicants && applicants.some(applicant => applicant.assessedChipEligible ||
                applicant.assessedMedicaidMAGIEligible || applicant.assessedMedicaidNonMAGIEligible || applicant.assessedBhpEligible);
        }
    };

    /**
     * Each group shows corresponding indicators based on eligibility props.
     * @param applicant
     * @param group
     * @returns {{}}
     */
    const calculateIndicatorsByGroup = (applicant, group) => {
        const {
            chipEligible,
            nativeAmerican,
            assessedMedicaidMAGIEligible,
            assessedMedicaidNonMAGIEligible,
            assessedChipEligible,
            medicaidMAGIEligible,
            medicaidNonMAGIEligible,
            seekingCoverage,
            exchangeEligible,
            emergencyMedicaidEligible = false,
            bhpEligible,
            assessedBhpEligible,
        } = applicant;

        const isMedicaidEligible = medicaidMAGIEligible || medicaidNonMAGIEligible;
        const isAssessedMedicaidEligible = assessedMedicaidMAGIEligible || assessedMedicaidNonMAGIEligible;
        const isNMWithMedicaid = isState.NM && isMedicaidEligible;
        const isNMWithAssessedMedicaid = isState.NM && isAssessedMedicaidEligible;
        const fmt = (key) => formatMessage(ELIGIBILITY_INDICATORS[key]);

        const sharedRules = [
            [bhpEligible,                  fmt('BHP')],
            [assessedBhpEligible,          fmt('ASSESSED_BHP')],
            [isAssessedMedicaidEligible,   fmt('ASSESSED_MEDICAID')],
            [assessedChipEligible,         fmt('CHIP_ASSESSED')],
            [isMedicaidEligible,           fmt('MEDICAID')],
            [nativeAmerican,               fmt('AI_AN')],
            [emergencyMedicaidEligible,    fmt('POTENTIALLY_EMERGENCY_MEDICAID_ELIGIBLE')],
        ];

        const group1Rules = [
            [bhpEligible,                                              fmt('BHP')],
            [assessedBhpEligible,                                      fmt('ASSESSED_BHP')],
            [!seekingCoverage,                                         fmt('NOT_SEEKING_COVERAGE')],
            [chipEligible && !exchangeEligible && !isNMWithMedicaid,   fmt('CHIP')],
            [isAssessedMedicaidEligible,                               fmt('ASSESSED_MEDICAID')],
            [assessedChipEligible && !isNMWithAssessedMedicaid,        fmt('CHIP_ASSESSED')],
            [isMedicaidEligible,                                       fmt('MEDICAID')],
            [emergencyMedicaidEligible,                                fmt('POTENTIALLY_EMERGENCY_MEDICAID_ELIGIBLE')],
        ];

        const rulesByGroup = {
            1: group1Rules,
            2: sharedRules,
            3: sharedRules,
        };

        const rules = rulesByGroup[group] ?? [];
        return rules.filter(([condition]) => condition).map(([, indicator]) => indicator);
    };

    /**
     * Returns only needed, filtered application data with precalculated indicators.
     * @param applicant
     * @param groupInd
     * @returns {{name: string, id: *, indicators: {}}}
     */
    const filterApplicantData = (applicant, groupInd) => {
        const {
            memberId,
            firstName,
            middleName,
            lastName,
            nameSuffix,
        } = applicant;

        return {
            id: memberId,
            name: `${firstName} ${middleName && middleName} ${lastName} ${nameSuffix && nameSuffix}`,
            indicators: calculateIndicatorsByGroup(applicant, groupInd)
        };
    };

    const populateMembers = (group, groupInd) => {
        if (!group || group.length < 0 ) return false;

        return group.reduce((acc, applicant) => {
            acc.push(filterApplicantData(applicant, groupInd));
            return acc;
        }, []);
    };
    

    const populateStatuses = (group, groupInd, aptc) => {

        const statuses = [];
        switch (groupInd) {
            case 1:
                statuses.push(formatMessage(ELIGIBILITY_STATUSES.NOT_ELIGIBLE_ENROLL, { exchangeName }));
                break;

            case 2:
                statuses.push(formatMessage(ELIGIBILITY_STATUSES.ELIGIBLE_QHP));
                
                // CSR
                checkMemberEligibility(ELIGIBILITY_OPTIONS.CSR_ELIGIBLE, group) &&
                statuses.push(formatMessage(ELIGIBILITY_STATUSES.ELIGIBLE_CSR, {
                    csrTooltip: (
                        <Tooltip
                            id='household_section.eligibility.status.csr'
                            content='household_section.tooltip_csr'
                            index={groupInd}
                        />
                    ),
                    turquoisePlansTooltip: groupHasTurquoiseEligibility(group) ? (
                        <Tooltip
                            id='householdSection.eligibility.status.turquoisePlans'
                            content='householdSection.tooltipTurquoisePlans'
                            index={groupInd}
                        />
                    ) : undefined,
                }));
                break;

            case 3:
                // APTC
                if (aptc !== null && checkMemberEligibility(ELIGIBILITY_OPTIONS.APTC_ELIGIBLE, group)) {
                    statuses.push(
                        <Tooltip
                            id={ELIGIBILITY_STATUSES.ELIGIBLE_APTC}
                            content='household_section.tooltip_aptc'
                            index={groupInd}
                        />
                    );
                    statuses.push(formatMessage('indportal.dashboard.householdSection.eligibility.status.eligibleAmount', { amount: renderAmount(aptc) }, 'b'));
                }
                // State subsidy
                if (stateSubsidy !== null && checkMemberEligibility(ELIGIBILITY_OPTIONS.STATE_SUBSIDY, group)) {
                    statuses.push(
                        <Tooltip
                            id={ELIGIBILITY_STATUSES.ELIGIBLE_SUBSIDY}
                            content='household_section.tooltip_state_subsidy'
                            index={groupInd}
                            dataAutomationId='state_subsidy_dashboard_household'
                        />
                    );
                    statuses.push(
                        <b data-automation-id='state_subsidy_dashboard_household_amount'>
                            <FormattedMessage
                                id='indportal.dashboard.householdSection.eligibility.status.eligibleAmount'
                                values={{ amount: renderAmount(stateSubsidy) }}
                            />
                        </b>
                    );
                }
                
                // CSR
                checkMemberEligibility(ELIGIBILITY_OPTIONS.CSR_ELIGIBLE, group) ?
                    statuses.push(formatMessage(ELIGIBILITY_STATUSES.ELIGIBLE_CSR_ANY_MEMBER, {
                        csrTooltip: (
                            <Tooltip
                                id='household_section.eligibility.status.csr'
                                content='household_section.tooltip_csr'
                                index={groupInd}
                            />
                        ),
                        turquoisePlansTooltip: groupHasTurquoiseEligibility(group) ? (
                            <Tooltip
                                id='householdSection.eligibility.status.turquoisePlans'
                                content='householdSection.tooltipTurquoisePlans'
                                index={groupInd}
                            />
                        ) : undefined,
                    })) : statuses.push(formatMessage(ELIGIBILITY_STATUSES.NOT_ELIGIBLE_CSR));

                // State subsidy 2
                checkMemberEligibility(ELIGIBILITY_OPTIONS.STATE_SUBSIDY_2, group) && statuses.push(formatMessage(ELIGIBILITY_STATUSES.ELIGIBLE_STATE_SUBSIDY_2, { stateSubsidy2Tooltip:
                    <Tooltip 
                        id='householdSection.eligibility.status.stateSubsidy2' 
                        content='householdSection.tooltipStateSubsidy2' 
                        index={groupInd} 
                    /> 
                }));

                // State subsidy 3
                checkMemberEligibility(ELIGIBILITY_OPTIONS.STATE_SUBSIDY_3, group) && statuses.push(formatMessage(ELIGIBILITY_STATUSES.ELIGIBLE_STATE_SUBSIDY_3, { stateSubsidy3Tooltip:
                    <Tooltip 
                        id='householdSection.eligibility.status.stateSubsidy3' 
                        content='householdSection.tooltipStateSubsidy3' 
                        index={groupInd} 
                    /> 
                }));
                
                break;
        }
        return statuses;
    };

    /**
     * Main function for distributing applicants into 3 groups.
     * @returns {[][]}
     */
    const createGroups = (applicants) => {
        if (!applicants || applicants.length < 0) return false;

        const group_1 = [];
        const group_2 = [];
        const group_3 = [];


        let aptcByGroup = applicants.reduce((accumulator, applicant) => {
            return accumulator + applicant.aptc;
        }, 0);

        applicants.map((applicant) => {
            const {
                aptcEligible,
                stateSubsidyEligible,
                exchangeEligible,
                seekingCoverage,
                chipEligible,
                medicaidMAGIEligible,
                medicaidNonMAGIEligible,
                assessedMedicaidMAGIEligible,
                assessedChipEligible,
                emergencyMedicaidEligible,
                stateSubsidy2Eligible,
                stateSubsidy3Eligible,
            } = applicant;
            if (aptcEligible || stateSubsidyEligible || stateSubsidy2Eligible || stateSubsidy3Eligible) {
                group_3.push(applicant);
            }

            else if (exchangeEligible && !aptcEligible && !stateSubsidyEligible) {
                group_2.push(applicant);
            }

            else if (!seekingCoverage || !exchangeEligible ||
                chipEligible || medicaidMAGIEligible || medicaidNonMAGIEligible ||
                assessedMedicaidMAGIEligible || assessedChipEligible || emergencyMedicaidEligible) {
                group_1.push(applicant);
            }
        });
        const formattedGroups = [];

        [group_1, group_2, group_3].forEach((group, index) => {
            formattedGroups.push({
                members: populateMembers(group, index + 1),
                statuses: populateStatuses(group, index + 1, aptcByGroup),
                isBhpEligibleDetermined: group.some(applicant => applicant.bhpEligible),
                isMedicaidDetermined: group.some(applicant => applicant.medicaidMAGIEligible || applicant.medicaidNonMAGIEligible),
            });
        });
        return formattedGroups;
    };


    const getHouseholdEligibility =  (applicants, index) => {
        let nonMemberITF = undefined;
        if (applicants) {
            nonMemberITF = applicants.map(applicant => applicant.taxHouseholdGroup === 'NON_ITF').includes(true);
        }

        switch (applicationStatus) {
            case APP_STATUSES.ER:
            case APP_STATUSES.PN:
            case APP_STATUSES.EN: {
                const groups = createGroups(applicants);
                return (
                    <div key={index} className="grid-row household-eligibility-group__tax-group">
                        <HouseholdEligibilityGroups
                            configs={configs}
                            groups={groups && groups.slice(0).reverse()}
                            isMNFullStack={isMNFullStack}
                        />
                        {
                            nonMemberITF && <HouseholdCTA />
                        }
                    </div>
                );
            }
            case null:
            case APP_STATUSES.OP:
            case APP_STATUSES.CL:
                return (
                    <div key={index} className="grid-row household-eligibility-group__tax-group">
                        <div className='grid-col-12 pr-3'>
                            {
                                isPreEligibility ? <FormattedMessage id="household_section.pre_eligibility"
                                    values={{
                                        preAPTC: preAPTC && <FormattedMessage id="household_section.preAPTC"/>,
                                        and: preAPTC && preCSR && <FormattedMessage id="common.and"/>,
                                        preCSR: preCSR && <FormattedMessage id="household_section.preCSR" />
                                    }} /> :
                                    <FormattedMessage id="household_section.default"/>
                            }
                        </div>
                    </div>
                );

            case APP_STATUSES.CC:
            case APP_STATUSES.UC:
            default:
                return (
                    <div key={index} className="grid-row household-eligibility-group__tax-group">
                        <div className='grid-col-12 pr-3'>
                            <FormattedMessage id="household_section.default"/>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div id="auto-household" className="household-eligibility gi-panel gi-member_portal_panel">
            <div className="">
                <h4 id="household_elig_header">
                    <FormattedMessage id="indportal.dashboard.householdSection.header"/>
                    { getConditionEligibilitySection() }
                </h4>
                <ConditionalRendering isTrue={showPublicProgramMessage()}>
                    <div className="grid-col-12 pr-3">
                        <FormattedMessage
                            id="household_section.public_program"
                            tagName="p"
                            values={{
                                stateName: STATE_NAMES[stateCode],
                                phone: PHONE,
                                br: <br />,
                            }}
                        />
                    </div>
                </ConditionalRendering>
            </div>
            { Object.keys(applicantByTaxHouseholdGroup).length < 1 && getHouseholdEligibility(null, undefined) }
            { Object.keys(applicantByTaxHouseholdGroup).length > 0 && applicantByTaxHouseholdGroup.map((applicants, index) => getHouseholdEligibility(applicants, index)) }
        </div>
    );
};
const mapStateToProps = state => ({
    environment: state.common.environment,
    dashboardModels: state.individualPortal.dashboard.dashboardModels,
    activeTab: state.individualPortal.dashboard.activeTab
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        showModal,
        checkActiveEnrollment,
        cloneApplication
    }, dispatch)
});


export default connect(mapStateToProps, mapDispatchToProps)(HouseholdEligibilityContainer);